import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function QuoteAccept() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let done = false;
    api.post(`/public/quotes/accept/${token}`)
      .then((r) => { if (!done) setState({ loading: false, data: r.data }); })
      .catch((e) => { if (!done) setState({ loading: false, error: e.response?.data?.detail || "This link is invalid or has expired." }); });
    return () => { done = true; };
  }, [token]);

  const money = (n, cur) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || ""}`;
  const d = state.data;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="quote-accept-page">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        {state.loading ? (
          <div className="flex flex-col items-center py-10 text-slate-500" data-testid="quote-accept-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm">Confirming your acceptance…</p>
          </div>
        ) : state.error ? (
          <div className="flex flex-col items-center text-center" data-testid="quote-accept-error">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50"><AlertTriangle className="h-7 w-7 text-amber-500" /></span>
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">We couldn’t open this quote</h1>
            <p className="mt-2 text-sm text-slate-500">{state.error}</p>
          </div>
        ) : d.expired ? (
          <div className="flex flex-col items-center text-center" data-testid="quote-accept-expired">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-50"><AlertTriangle className="h-7 w-7 text-orange-500" /></span>
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">This quote has expired</h1>
            <p className="mt-2 text-sm text-slate-500">
              Quote #{d.number} is no longer valid{d.valid_until ? ` (expired ${String(d.valid_until).slice(0, 10)})` : ""}. Please contact your account team for an updated quote.
            </p>
            <p className="mt-6 text-xs text-slate-400">{d.brand_name}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center" data-testid="quote-accept-success">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50"><CheckCircle2 className="h-9 w-9 text-green-600" /></span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">
              {d.already_accepted ? "Already accepted" : "Quote accepted!"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {d.already_accepted
                ? `Quote #${d.number} was already accepted. Thank you!`
                : `Thank you, ${d.client_name || "valued client"}. Your acceptance has been recorded and your account team has been notified.`}
            </p>
            <div className="mt-6 w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
              <div className="text-xs uppercase tracking-wide text-slate-400">Quote #{d.number}</div>
              <div className="mt-0.5 font-medium text-slate-800">{d.name}</div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-slate-500">Total</span>
                <span className="font-semibold text-slate-900" data-testid="quote-accept-total">{money(d.total, d.currency)}</span>
              </div>
            </div>
            <p className="mt-6 text-xs text-slate-400">{d.brand_name}</p>
          </div>
        )}
      </div>
    </div>
  );
}
